import React, { useState, useEffect, useCallback } from 'react';
import { X, ExternalLink, MessageCircle, PhoneCall } from 'lucide-react';
import { Popup } from '../../utils/supabase';
import { useActivePopups } from '../../hooks/usePopups';
import { safeExternalUrl } from '../../utils/sanitize';

interface PopupDisplayProps {
  currentPath?: string;
}

interface DisplayedPopupProps {
  popup: Popup;
  onClose: () => void;
  onButtonClick: () => void;
}

const DisplayedPopup: React.FC<DisplayedPopupProps> = ({ popup, onClose, onButtonClick }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show popup after delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, (popup.delay_seconds || 0) * 1000);

    return () => clearTimeout(timer);
  }, [popup.delay_seconds]);

  const handleClose = useCallback(() => {
    // Call parent's onClose immediately - let parent handle the unmounting
    onClose();
  }, [onClose]);

  useEffect(() => {
    // Auto close popup if specified
    if (popup.auto_close_seconds && isVisible) {
      const timer = setTimeout(() => {
        handleClose();
      }, popup.auto_close_seconds * 1000);

      return () => clearTimeout(timer);
    }
  }, [popup.auto_close_seconds, isVisible, handleClose]);

  useEffect(() => {
    // Handle escape key
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll for modal popups
      if (popup.popup_type === 'modal') {
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (popup.popup_type === 'modal') {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isVisible, popup.popup_type, handleClose]);

  const handleButtonClick = useCallback(() => {
    onButtonClick();
    
    const safeUrl = safeExternalUrl(popup.button_url);
    if (popup.button_action === 'redirect' && safeUrl) {
      window.open(safeUrl, '_blank', 'noopener,noreferrer');
    } else if (popup.button_action === 'close') {
      handleClose();
    }
    // For custom actions, the parent component should handle the logic
  }, [popup.button_action, popup.button_url, onButtonClick, handleClose]);

  const getPositionClasses = () => {
    const base = 'fixed z-50';
    
    switch (popup.popup_type) {
      case 'modal':
        return `${base} inset-0 flex items-center justify-center p-4`;
      
      case 'banner':
        switch (popup.position) {
          case 'top':
            return `${base} top-0 left-0 right-0`;
          case 'bottom':
            return `${base} bottom-0 left-0 right-0`;
          default:
            return `${base} top-0 left-0 right-0`;
        }
      
      case 'slide-in':
        switch (popup.position) {
          case 'left':
            return `${base} left-0 top-1/2 transform -translate-y-1/2`;
          case 'right':
            return `${base} right-0 top-1/2 transform -translate-y-1/2`;
          case 'top':
            return `${base} top-0 left-1/2 transform -translate-x-1/2`;
          case 'bottom':
            return `${base} bottom-0 left-1/2 transform -translate-x-1/2`;
          default:
            return `${base} right-0 top-1/2 transform -translate-y-1/2`;
        }
      
      case 'corner':
        switch (popup.position) {
          case 'top-left':
            return `${base} top-4 left-4`;
          case 'top-right':
            return `${base} top-4 right-4`;
          case 'bottom-left':
            return `${base} bottom-4 left-4`;
          case 'bottom-right':
          default:
            return `${base} bottom-4 right-4`;
        }
      
      default:
        return `${base} inset-0 flex items-center justify-center p-4`;
    }
  };

  const getSizeClasses = () => {
    if (popup.popup_type === 'banner') {
      return 'w-full';
    }
    
    // If it's an image-only popup, let it size to the image with larger max dimensions
    if (popup.image_url && !popup.button_text) {
      return 'w-auto max-w-[94vw] max-h-[94vh]';
    }
    
    // For image popups with buttons, make them larger
    if (popup.image_url) {
      return 'w-auto max-w-[90vw] max-h-[90vh]';
    }
    
    switch (popup.width) {
      case 'sm':
        return 'w-80 max-w-sm';
      case 'md':
        return 'w-96 max-w-md';
      case 'lg':
        return 'w-[32rem] max-w-2xl';
      case 'xl':
        return 'w-[48rem] max-w-4xl';
      case 'full':
        return 'w-full max-w-full';
      default:
        return 'w-96 max-w-md';
    }
  };

  const getAnimationClasses = () => {
    switch (popup.popup_type) {
      case 'modal':
        return isVisible 
          ? 'animate-in fade-in-0 zoom-in-95 duration-300' 
          : 'animate-out fade-out-0 zoom-out-95 duration-300';
      
      case 'banner':
        const isTop = popup.position === 'top';
        return isVisible
          ? `animate-in ${isTop ? 'slide-in-from-top' : 'slide-in-from-bottom'} duration-300`
          : `animate-out ${isTop ? 'slide-out-to-top' : 'slide-out-to-bottom'} duration-300`;
      
      case 'slide-in':
        const slideDirection = popup.position === 'left' ? 'slide-in-from-left' : 'slide-in-from-right';
        const slideOutDirection = popup.position === 'left' ? 'slide-out-to-left' : 'slide-out-to-right';
        return isVisible
          ? `animate-in ${slideDirection} duration-300`
          : `animate-out ${slideOutDirection} duration-300`;
      
      case 'corner':
        return isVisible
          ? 'animate-in slide-in-from-bottom-2 fade-in-0 duration-300'
          : 'animate-out slide-out-to-bottom-2 fade-out-0 duration-300';
      
      default:
        return isVisible 
          ? 'animate-in fade-in-0 zoom-in-95 duration-300' 
          : 'animate-out fade-out-0 zoom-out-95 duration-300';
    }
  };

  // Component always renders when mounted - parent controls mounting/unmounting

  const popupStyle = {
    backgroundColor: popup.background_color || '#FFFFFF',
    color: popup.text_color || '#1F2937',
    borderColor: popup.border_color || '#E5E7EB',
  };

  const buttonStyle = {
    backgroundColor: popup.button_color || '#DC2626',
    color: popup.button_text_color || '#FFFFFF',
  };

  return (
    <div className={getPositionClasses()}>
      {/* Backdrop for modal */}
      {popup.popup_type === 'modal' && (
        <div
          className={`fixed inset-0 bg-slate-950/75 backdrop-blur-sm transition-opacity duration-300 ${
            isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={handleClose}
        />
      )}
      
      {/* Popup Content */}
      <div
        className={`
          relative border border-gray-100 bg-white p-3.5 sm:p-4 shadow-[0_32px_100px_rgba(7,16,29,0.45)]
          ${getSizeClasses()}
          ${getAnimationClasses()}
          ${popup.popup_type === 'banner' ? 'rounded-none' : ''}
          ${popup.popup_type === 'corner' ? 'max-w-sm' : ''}
          ${popup.image_url && !popup.button_text ? 'overflow-hidden rounded-3xl' : 'rounded-3xl'}
        `}
        style={popupStyle}
      >
        {/* Close Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }}
          className="absolute right-3.5 top-3.5 sm:right-5 sm:top-5 z-[60] flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-slate-900/80 text-white border border-white/30 shadow-xl backdrop-blur-md transition-all duration-200 hover:scale-110 hover:bg-slate-950 focus:outline-none focus:ring-2 focus:ring-crimson"
          aria-label="Close popup"
          type="button"
        >
          <X size={18} />
        </button>

        {/* Content */}
        <div className={`${popup.popup_type === 'banner' ? 'py-4' : ''}`}>
          {/* Image-focused popup */}
          {popup.image_url && !popup.button_text ? (
            <div className="relative bg-white">
              <img
                src={popup.image_url}
                alt=""
                className="block h-auto max-h-[72vh] w-full max-w-[94vw] rounded-2xl object-contain shadow-sm"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <div className="mt-3.5 flex flex-col gap-3 rounded-2xl bg-gray-50/90 border border-gray-100 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                <div className="min-w-0">
                  <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-crimson">Need Guidance?</p>
                  <p className="mt-0.5 text-xs sm:text-sm font-semibold text-gray-700">Talk with NWLC advisors before you apply.</p>
                </div>
                <a
                  href="/contact"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-xs sm:text-sm font-extrabold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-crimson focus:outline-none focus:ring-2 focus:ring-crimson/30"
                >
                  <PhoneCall size={16} aria-hidden="true" />
                  <span>Contact Us</span>
                </a>
              </div>
            </div>
          ) : (
            <div>
              {/* Image */}
              {popup.image_url && (
                <div className="overflow-hidden rounded-2xl bg-gray-50 shadow-inner">
                  <img
                    src={popup.image_url}
                    alt=""
                    className="h-auto max-h-[64vh] w-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Buttons */}
              <div className="mt-3.5 flex flex-col gap-2.5 rounded-2xl bg-gray-50/90 border border-gray-100 p-3 sm:flex-row sm:items-center sm:justify-end">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-xs sm:text-sm font-extrabold text-gray-800 shadow-sm transition-all hover:-translate-y-0.5 hover:border-red-200 hover:text-crimson focus:outline-none focus:ring-2 focus:ring-crimson/30"
                >
                  <MessageCircle size={16} aria-hidden="true" />
                  <span>Contact Us</span>
                </a>
                {popup.button_text && (
                  <button
                    onClick={handleButtonClick}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 text-xs sm:text-sm font-extrabold shadow-md transition-all hover:-translate-y-0.5 hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-crimson/30"
                    style={buttonStyle}
                    type="button"
                  >
                    <span>{popup.button_text}</span>
                    {popup.button_action === 'redirect' && <ExternalLink size={15} />}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-black/5" aria-hidden="true" />
      </div>
    </div>
  );
};

export const PopupDisplay: React.FC<PopupDisplayProps> = ({ currentPath }) => {
  const { activePopups, recordInteraction, dismissPopup, clickPopup } = useActivePopups(currentPath);
  const [closedPopupIds, setClosedPopupIds] = useState<string[]>([]);
  const [viewedPopupIds, setViewedPopupIds] = useState<string[]>([]);

  // Filter out closed popups
  const availablePopups = activePopups.filter(popup =>
    popup.id && !closedPopupIds.includes(popup.id)
  );

  // Get the first available popup that hasn't been viewed
  const currentPopup = availablePopups.find(popup =>
    popup.id && !viewedPopupIds.includes(popup.id)
  ) || availablePopups[0]; // Fallback to first available if all have been viewed

  useEffect(() => {
    if (currentPopup && currentPopup.id && !viewedPopupIds.includes(currentPopup.id)) {
      setViewedPopupIds(prev => [...prev, currentPopup.id!]);
      recordInteraction(currentPopup.id!, 'viewed');
    }
  }, [currentPopup, viewedPopupIds, recordInteraction]);

  const handleClosePopup = useCallback(() => {
    if (currentPopup && currentPopup.id) {
      // Add to closed list to prevent re-display
      setClosedPopupIds(prev => [...prev, currentPopup.id!]);
      // Record dismissal
      dismissPopup(currentPopup.id!);
    }
  }, [currentPopup, dismissPopup]);

  const handleButtonClick = useCallback(() => {
    if (currentPopup && currentPopup.id) {
      clickPopup(currentPopup.id!);
    }
  }, [currentPopup, clickPopup]);

  // If no current popup, render nothing
  if (!currentPopup) {
    return null;
  }

  return (
    <DisplayedPopup
      key={currentPopup.id} // Add key to force re-render on popup change
      popup={currentPopup}
      onClose={handleClosePopup}
      onButtonClick={handleButtonClick}
    />
  );
};

export default PopupDisplay;
